import { ColorDTO, colorDtoToHex } from '@model/dynamicTimeTable/ColorDTO';
import { type JSONContent } from '@tiptap/core';
import {
  AnnouncementInlineNodeDTO,
  AnnouncementMarkDTO,
  AnnouncementParagraphNodeDTO,
  AnnouncementRichTextContentDTO,
  AnnouncementTextAlignDTO,
} from '@/model';

const hexToColorDto = (hex: string): ColorDTO | undefined =>
  Object.values(ColorDTO).find(
    (color) => colorDtoToHex(color).toLowerCase() === hex.toLowerCase(),
  );

const isTextAlign = (value: unknown): value is AnnouncementTextAlignDTO =>
  value === 'left' || value === 'center' || value === 'right' || value === 'justify';

const mapMarkToDTO = (
  mark: NonNullable<JSONContent['marks']>[number],
  fontIdByName: Map<string, number>,
): AnnouncementMarkDTO | undefined => {
  switch (mark.type) {
    case 'bold':
      return { type: 'bold' };
    case 'italic':
      return { type: 'italic' };
    case 'underline':
      return { type: 'underline' };
    case 'textStyle': {
      const color = mark.attrs?.color ? hexToColorDto(mark.attrs.color) : undefined;
      const fontId = fontIdByName.get(mark.attrs?.fontFamily);

      const fontSize: string | undefined = mark.attrs?.fontSize ?? undefined;

      if (color === undefined && fontId === undefined && fontSize === undefined) {
        return undefined;
      }

      return {
        type: 'textStyle',
        attrs: {
          ...(color !== undefined ? { color } : {}),
          ...(fontId !== undefined ? { fontId } : {}),
          ...(fontSize !== undefined ? { fontSize } : {}),
        },
      };
    }
    case 'highlight': {
      const color = mark.attrs?.color ? hexToColorDto(mark.attrs.color) : undefined;
      return color === undefined ? undefined : { type: 'highlight', attrs: { color } };
    }
    default:
      return undefined;
  }
};

const mapInlineNodeToDTO = (
  node: JSONContent,
  fontIdByName: Map<string, number>,
): AnnouncementInlineNodeDTO | undefined => {
  if (node.type === 'hardBreak') {
    return { type: 'hardBreak' };
  }

  if (node.type === 'text' && node.text) {
    const marks = node.marks
      ?.map((mark) => mapMarkToDTO(mark, fontIdByName))
      .filter((mark): mark is AnnouncementMarkDTO => mark !== undefined);

    return {
      type: 'text',
      text: node.text,
      ...(marks?.length ? { marks } : {}),
    };
  }

  return undefined;
};

const mapParagraphToDTO = (
  node: JSONContent,
  fontIdByName: Map<string, number>,
): AnnouncementParagraphNodeDTO => {
  const textAlign = isTextAlign(node.attrs?.textAlign) ? node.attrs.textAlign : undefined;
  const content = node.content
    ?.map((child) => mapInlineNodeToDTO(child, fontIdByName))
    .filter((child): child is AnnouncementInlineNodeDTO => child !== undefined);

  return {
    type: 'paragraph',
    ...(textAlign ? { attrs: { textAlign } } : {}),
    ...(content?.length ? { content } : {}),
  };
};

export const toAnnouncementRichTextContentDTO = (
  json: JSONContent,
  fontIdByName: Map<string, number>,
): AnnouncementRichTextContentDTO => ({
  type: 'doc',
  content: (json.content ?? [])
    .filter((node) => node.type === 'paragraph')
    .map((node) => mapParagraphToDTO(node, fontIdByName)),
});

export const toPlainText = (json: JSONContent): string =>
  (json.content ?? [])
    .filter((node) => node.type === 'paragraph')
    .map((paragraph) =>
      (paragraph.content ?? [])
        .map((node) => {
          if (node.type === 'text') {
            return node.text ?? '';
          }
          if (node.type === 'hardBreak') {
            return '\n';
          }
          return '';
        })
        .join(''),
    )
    .join('\n');

const mapMarkToTiptap = (
  mark: AnnouncementMarkDTO,
  fontNameById: Map<number, string>,
): NonNullable<JSONContent['marks']>[number] | undefined => {
  switch (mark.type) {
    case 'bold':
      return { type: 'bold' };
    case 'italic':
      return { type: 'italic' };
    case 'underline':
      return { type: 'underline' };
    case 'textStyle': {
      const { color, fontId, fontSize } = mark.attrs;
      return {
        type: 'textStyle',
        attrs: {
          ...(color != null ? { color: colorDtoToHex(color) } : {}),
          ...(fontId != null ? { fontFamily: fontNameById.get(fontId) } : {}),
          ...(fontSize != null ? { fontSize } : {}),
        },
      };
    }
    case 'highlight':
      return mark.attrs.color != null
        ? { type: 'highlight', attrs: { color: colorDtoToHex(mark.attrs.color) } }
        : undefined;
  }
};

const mapInlineNodeToTiptap = (
  node: AnnouncementInlineNodeDTO,
  fontNameById: Map<number, string>,
): JSONContent => {
  if (node.type === 'hardBreak') {
    return { type: 'hardBreak' };
  }

  const marks = node.marks
    ?.map((mark) => mapMarkToTiptap(mark, fontNameById))
    .filter(
      (mark): mark is NonNullable<JSONContent['marks']>[number] => mark !== undefined,
    );

  return {
    type: 'text',
    text: node.text,
    ...(marks?.length ? { marks } : {}),
  };
};

const mapParagraphToTiptap = (
  node: AnnouncementParagraphNodeDTO,
  fontNameById: Map<number, string>,
): JSONContent => ({
  type: 'paragraph',
  ...(node.attrs?.textAlign ? { attrs: { textAlign: node.attrs.textAlign } } : {}),
  ...(node.content?.length
    ? { content: node.content.map((child) => mapInlineNodeToTiptap(child, fontNameById)) }
    : {}),
});

export const fromAnnouncementRichTextContentDTO = (
  dto: AnnouncementRichTextContentDTO,
  fontNameById: Map<number, string>,
): JSONContent => ({
  type: 'doc',
  content: dto.content.map((node) => mapParagraphToTiptap(node, fontNameById)),
});
